import React from "react";
import { Box, Typography } from "@mui/material";

const serverType = process.env.REACT_APP_SERVER_TYPE || "bigfoot";

const accentColors: Record<string, string> = {
  bigfoot: "#00B4D8",
  spacex: "#8B5CF6",
  industrial: "#F59E0B",
};

const gradients: Record<string, string> = {
  bigfoot: "linear-gradient(135deg, #0B1929 0%, #1A2B45 50%, #0B1929 100%)",
  spacex: "linear-gradient(135deg, #0B1929 0%, #1E1145 50%, #0B1929 100%)",
  industrial: "linear-gradient(135deg, #0B1929 0%, #2D1F0B 50%, #0B1929 100%)",
};

const loginImages: Record<string, string> = {
  bigfoot: "/dealer.jpg",
  dealer: "/dealer.jpg",
  spacex: "/spacex.jpg",
  industrial: "/industrial-login.jpg",
};

const taglines: Record<string, string> = {
  bigfoot: "Your direct line to BigFoot pricing and quotes",
  spacex: "Your direct line to BigFoot pricing and quotes",
  industrial: "Your direct line to BigFoot pricing and quotes",
};

interface AuthLayoutProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}

export const AuthLayout: React.FC<AuthLayoutProps> = ({ title, subtitle, children }) => {
  const accent = accentColors[serverType] || accentColors.bigfoot;
  const gradient = gradients[serverType] || gradients.bigfoot;
  const tagline = taglines[serverType] || taglines.bigfoot;
  const loginImage = loginImages[serverType] || loginImages.bigfoot;

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* Left branded panel with background image */}
      <Box
        sx={{
          width: "60%",
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          justifyContent: "flex-end",
          alignItems: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Background image */}
        <Box
          component="img"
          src={loginImage}
          alt="BigFoot Mobile Systems"
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
        {/* Subtle brand color overlay */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: accent,
            opacity: 0.15,
          }}
        />
        {/* Dark gradient overlay for text readability */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.1) 50%, rgba(0,0,0,0.15) 100%)",
          }}
        />

        {/* Text over image */}
        <Box sx={{ position: "relative", zIndex: 1, p: 6, textAlign: "center" }}>
          <Typography
            variant="h1"
            sx={{
              color: "#FFFFFF",
              fontSize: { md: "2.5rem", lg: "3rem" },
              fontWeight: 700,
              mb: 1,
              textShadow: "0 2px 8px rgba(0,0,0,0.4)",
            }}
          >
            Welcome to{" "}
            <Box component="span" sx={{ color: accent }}>
              BigFoot
            </Box>
          </Typography>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.8)",
              fontSize: "1.125rem",
              textShadow: "0 1px 4px rgba(0,0,0,0.4)",
            }}
          >
            {tagline}
          </Typography>
        </Box>
      </Box>

      {/* Right form panel */}
      <Box
        sx={{
          width: { xs: "100%", md: "40%" },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          bgcolor: "#FFFFFF",
          p: { xs: 3, sm: 6 },
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 400 }}>
          <Typography
            variant="h2"
            sx={{
              color: "#0B1929",
              fontSize: "1.75rem",
              fontWeight: 700,
              mb: 0.5,
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              sx={{
                color: "#64748B",
                fontSize: "0.938rem",
                mb: 3,
              }}
            >
              {subtitle}
            </Typography>
          )}
          {children}
        </Box>
      </Box>
    </Box>
  );
};
